import { useEffect, useMemo } from 'react'

import { useFetchEmrLinkedProvidersQuery } from 'features/Patients/state/api/patientApi'

import { useFetchSeriesListQuery } from '../../api/series.api'
import { toSeriesApiError } from '../../api/series.transform'
import type { SeriesListFilter } from '../../api/series.types'
import { SERIES_LIST_PER_PAGE } from '../../series.constants'

const FORBIDDEN = 403
const TARGET_NOT_IN_ACCOUNT = 'target_not_in_account'

export type ProvidersState = 'loading' | 'error' | 'ready'

type Args = {
  siteId: string
  filter: SeriesListFilter
  setFilter: (patch: Partial<SeriesListFilter>) => void
}

const useProviders = () => {
  const { data, isLoading, isError } = useFetchEmrLinkedProvidersQuery()
  const providers = useMemo(() => (data ?? []).map(({ id, name }) => ({ id, name })), [data])
  const state: ProvidersState = isError ? 'error' : isLoading ? 'loading' : 'ready'
  return { providers, providersState: state }
}

// The list query plus its recoveries: a page past the end and a doctor outside the account.
export const useSeriesListData = ({ siteId, filter, setFilter }: Args) => {
  const { providers, providersState } = useProviders()
  // A failed provider list shows "All providers", so the request must not keep a stale doctor.
  const requestFilter = useMemo(
    () => (providersState === 'error' ? { ...filter, doctorId: null } : filter),
    [filter, providersState],
  )
  const list = useFetchSeriesListQuery(
    { siteId, filter: requestFilter },
    { refetchOnMountOrArgChange: true },
  )
  const current = list.currentData

  const apiError = list.isError ? toSeriesApiError(list.error) : null
  const dropsDoctor =
    apiError?.status === FORBIDDEN &&
    apiError.code === TARGET_NOT_IN_ACCOUNT &&
    requestFilter.doctorId !== null

  useEffect(() => {
    if (dropsDoctor) setFilter({ doctorId: null })
  }, [dropsDoctor, setFilter])

  useEffect(() => {
    if (!current || current.rows.length > 0 || current.meta.total === 0) return
    const last = Math.max(1, Math.ceil(current.meta.total / SERIES_LIST_PER_PAGE))
    if (filter.page > last) setFilter({ page: last })
  }, [current, filter.page, setFilter])

  return {
    providers,
    providersState,
    page: current,
    counts: list.data?.counts ?? null,
    isLoading: !current && (!list.isError || dropsDoctor),
    isError: list.isError && !dropsDoctor && !list.isFetching,
    refetch: list.refetch,
  }
}
